import { graphicStore } from "@/global/store/graphic-store";
import { BlockT, ModelBase, PointT } from "pytha"
import { useContext, useEffect, useRef, useState } from "react";
import './device-pop-window.less';
import { ReactComponent as CloseSvg } from './icons/close.svg';
import { getDataBoxByPrimitiveUuid, getDataBoxDetail } from "@/api/device/data-box";
import { getSensorByPrimitiveUuid } from "@/api/device/sensor";
import { DataBox } from "@/global/interface/device/data-box";
import { Sensor } from "@/global/interface/device/sensor";
import TnMessage from "@/global/components/message";
import { getDataBoxTypeName } from "@/enums/data-box-type";
import { Skeleton } from "antd";
import { CommonTabbarContext, SideContext } from "@/App";
import { TAB_TYPE } from "@/enums/tab-type";
import { CommonTabbarState } from "@/global/components/common-tabbar";
import { DEVICE_TYPE } from "@/enums/device-type";
import { SideStatus } from "@/global/store/side-store";
import { getMonitorObjectWithParamDqsByProjectId } from "@/api/monitor-object/monitor-object";
import { useParams } from "react-router-dom";
import { MonitorObject } from "@/global/interface/monitor-object/monitor-object";
import classNames from "classnames";
import { MonitorParameter } from "@/global/interface/monitor-object/monitor-parameter";
import { multiUpdateMonitorParameterDqs } from "@/api/monitor-object/monitor-parameter";

interface IPosition {
    x: number,
    y: number
}
interface IProps {
    entity: ModelBase
    needRender: boolean
    setNeedReder: (value: boolean) => void
}
export default function DeviceDetailPopWindow(props: IProps) {
    const { projectId } = useParams();
    const ref = useRef();
    const [position, setPosition] = useState<IPosition>(null); // 气泡窗位置
    const [show, setShow] = useState<boolean>(true); // 是否显示气泡窗
    const [loading, setLoading] = useState<boolean>(false); // loading
    const [dataBox, setDataBox] = useState<DataBox>(null); // 采集箱
    const [sensor, setSensor] = useState<Sensor>(null); // 传感器
    const commonTabbarContext = useContext<CommonTabbarState>(CommonTabbarContext); // tab上下文
    const sideContext = useContext<SideStatus>(SideContext); // 侧边栏上下文
    const [currentPanel, setCurrentPanel] = useState<'detail' | 'param'>('detail'); // 当前展示页面
    const [list, setList] = useState<MonitorObject[]>([]); // 监测对象列表
    const [selectedParams, setSelectedParams] = useState<MonitorParameter[]>([]); // 选中的监测参数

    // 监听位置移动
    useEffect(() => {
        if (!graphicStore?.extraContext?.getCurrentViewEditor()) return;
        graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onPanChanged.add(computePosition);
        graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onRotateChanged.add(computePosition);
        graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onZoomChanged.add(computePosition);
        return () => {
            if (!graphicStore?.extraContext?.getCurrentViewEditor()) return;
            graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onPanChanged.remove(computePosition);
            graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onRotateChanged.remove(computePosition);
            graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onZoomChanged.remove(computePosition);
        }
    }, [graphicStore?.extraContext?.getCurrentViewEditor()?.camera])

    // 监听传入图元
    useEffect(() => {
        if (!props?.entity) return;
        computePosition();
        getDeviceDetail();
    }, [props.entity])

    useEffect(() => {
        if (currentPanel === 'param') {
            getMonitorObjects();
        }
    }, [props.needRender])

    // 计算弹窗位置
    const computePosition = () => {
        if (!props?.entity?.uuid) return;
        // 当前图元基点坐标
        let basePoint = (graphicStore.extraContext.getCurrentViewEditor().entityDict[props.entity.uuid] as BlockT).clone().position;
        // 转换为屏幕坐标
        basePoint.project(graphicStore.extraContext.getCurrentViewEditor().camera);
        let viewport = document.getElementById('viewport');
        let screenX = (basePoint.x + 1) * viewport.offsetWidth / 2;
        let screenY = (-basePoint.y + 1) * viewport.offsetHeight / 2;
        // // 保存组件位置
        let popupWidth = 320;
        let popupHeight = 250;
        let position = {
            x: screenX - popupWidth / 2,
            y: screenY - popupHeight - 50
        };
        setPosition(position);
    }

    // 获取设备详情
    const getDeviceDetail = () => {
        if (!props?.entity) return;
        if (props.entity.type === 'DvrT' || props.entity.type === 'SerialT'|| props.entity.type === 'IpcT') {
            setLoading(true);
            getDataBoxByPrimitiveUuid(props.entity.uuid).then(res => {
                if (res.status === 200) {
                    let data: DataBox = res.data || null;
                    setDataBox(data);
                }
            }).catch(err => {
                TnMessage.error("查询失败");
            }).finally(() => {
                setLoading(false);
            })
        } else if (props.entity.type === 'AnemoclinographT' || props.entity.type === 'MonitorT' || props.entity.type === 'FluviographT') {
            setLoading(true);
            getSensorByPrimitiveUuid(props.entity.uuid).then(res => {
                let data: Sensor = res.data || null;
                setSensor(data);
            }).catch(err => {
                TnMessage.error("查询失败");
            }).finally(() => {
                setLoading(false);
            })

        }
    }

    // 获取监测对象
    const getMonitorObjects = () => {
        setLoading(true);
        getMonitorObjectWithParamDqsByProjectId(+projectId).then(res => {
            let data: MonitorObject[] = res.data || [];
            let list: MonitorObject[] = [];
            const set = new Set<number>();
            // 存在dq的sensorId为空，并且存在对应监测参数时才添加
            data?.forEach(monitorObject => {
                if (monitorObject?.rootMonitorParameters) {
                    monitorObject?.rootMonitorParameters?.forEach(rootParam => {
                        if (rootParam?.subParametersDq) {
                            // sensorId为空
                            const hasEmptySensorId = rootParam?.subParametersDq?.some(subParam => !subParam?.sensorId);
                            // 有symbol
                            const hasSymbolDqs = rootParam?.subParametersDq?.filter(item => sensor?.sensorParameters?.some(sp => sp?.symbol === item?.symbol));
                            if (hasEmptySensorId && hasSymbolDqs?.length > 0 && !set.has(monitorObject.id)) {
                                rootParam.subParametersDq = hasSymbolDqs;
                                list.push(monitorObject);
                                set.add(monitorObject.id);
                            }
                        }
                    });
                }
            });
            setList(list);
        }).catch(err => {
            if (err.data) {
                TnMessage.error(err.data);
            } else {
                TnMessage.error("获得数据失败")
            }
        }).finally(() => {
            setLoading(false);
        })
    }

    // tab跳转至设备详情页,side跳转设备表
    const showDeviceDetail = () => {
        if (dataBox && (props.entity.type === 'DvrT' || props.entity.type === 'SerialT'|| props.entity.type === 'IpcT' )) {
            commonTabbarContext.navigate(`${TAB_TYPE.DEVICE}/${dataBox?.id}/${DEVICE_TYPE.DATA_BOX}`);
            sideContext.setSelectedPanelValue("device-list");
        } else if (sensor && (props.entity.type === 'AnemoclinographT' || props.entity.type === 'MonitorT' || props.entity.type === 'FluviographT')) {
            commonTabbarContext.navigate(`${TAB_TYPE.DEVICE}/${sensor?.id}/${DEVICE_TYPE.SENSOR}`)
            sideContext.setSelectedPanelValue("device-list");
        }
    }

    // 按下选参数按钮
    const selectParamClick = () => {
        setCurrentPanel("param");
        getMonitorObjects();
        setSelectedParams([]);
    }

    // 为直接参数选择传感器
    const selectDq = (dq: MonitorParameter) => {
        let idx = selectedParams?.findIndex(item => item.id === dq.id);
        if (idx === -1) {
            selectedParams.push(dq);
            setSelectedParams([...selectedParams]);
        } else {
            selectedParams.splice(idx, 1);
            setSelectedParams([...selectedParams]);
        }
    }

    // 按下完成按钮
    const saveBtnClick = () => {
        if (selectedParams?.length === 0 || !sensor) {
            setCurrentPanel('detail');
            setSelectedParams([]);
            return;
        }
        setLoading(true);
        selectedParams?.forEach(item => {
            item.sensorType = sensor?.type;
            item.sensorId = sensor?.id;
            item.globalSensorId = sensor?.globalSensorId;
            let sensorParameter = sensor?.sensorParameters?.find(sp => sp.symbol === item?.symbol);
            item.sensorParameterId = sensorParameter.id;
        })
        multiUpdateMonitorParameterDqs(+projectId, selectedParams).then(res => {
            if (res.status === 200) {
                TnMessage.success("选择成功");
                props.setNeedReder(!props?.needRender);
            }
        }).catch(err => {
            TnMessage.error("选择参数失败");
        }).finally(() => {
            setCurrentPanel('detail');
            setSelectedParams([]);
            setLoading(false);
        })


    }

    return (
        <div className="device-detail-pop" ref={ref} style={{ display: show ? 'flex' : 'none', position: `absolute`, left: position?.x, top: position?.y }}>
            <div className="pop-header">
                {dataBox && <span>{dataBox?.code || ''}</span>}
                {sensor && <span>{sensor?.code || ''}</span>}
                <CloseSvg onClick={() => setShow(false)} />
            </div>
            <Skeleton loading={loading}>
                {currentPanel === 'detail' && <div className="pop-body">
                    {dataBox && <>
                        <div className="pop-item">
                            <div className="pop-left">型号</div>
                            <div className="pop-right">{dataBox?.marking || ''}</div>
                        </div>
                        <div className="pop-item">
                            <div className="pop-left">厂商</div>
                            <div className="pop-right">{dataBox?.manufacturer || ''}</div>
                        </div>
                        <div className="pop-item">
                            <div className="pop-left">通道数量</div>
                            <div className="pop-right">{dataBox?.channelCount || ''}</div>
                        </div>
                        <div className="pop-item">
                            <div className="pop-left">类型</div>
                            <div className="pop-right">{getDataBoxTypeName(dataBox?.type) || ''}</div>
                        </div>
                    </>}
                    {sensor && <>
                        <div className="pop-item">
                            <div className="pop-left">型号</div>
                            <div className="pop-right">{sensor?.marking || ''}</div>
                        </div>
                        <div className="pop-item">
                            <div className="pop-left">厂商</div>
                            <div className="pop-right">{sensor?.manufacturer || ''}</div>
                        </div>
                        <div className="pop-item">
                            <div className="pop-left">测量范围</div>
                            <div className="pop-right">{sensor?.range || ''}</div>
                        </div>
                        <div className="pop-item">
                            <div className="pop-left">测量精度</div>
                            <div className="pop-right">{sensor?.precision || ''}</div>
                        </div>
                    </>}
                    <div className="another-row">
                        <div className="another-detail" onClick={showDeviceDetail}>更多详情</div>
                        {sensor && <div className="another-detail" style={{ marginRight: 16 }} onClick={selectParamClick}>选参数</div>}
                    </div>
                </div>}
                {currentPanel === 'param' && <div className="param-body">
                    <div className="param-block-area">
                        <>
                            {list?.length > 0 && list?.map((mo: MonitorObject, mopKey) => (
                                <div className="param-block" key={mopKey}>
                                    <div className="objname">{mo?.name || ''}</div>
                                    {mo?.rootMonitorParameters?.map((rmp: MonitorParameter, rmpKey) => (
                                        <>
                                            <div className="root-param" key={rmpKey}>{`${rmp?.encoding || ''} ${rmp?.name || ''} ${rmp?.code || ''}`}</div>
                                            {rmp?.subParametersDq?.map((dq: MonitorParameter, dqKey) => {
                                                if (dq?.sensorId) return <></>
                                                // TODO 这个参数不在传感器中（根据symbol判断）
                                                if (!sensor?.sensorParameters?.some(sp => sp?.symbol === dq?.symbol)) return <></>
                                                return (
                                                    <div className="dq-param-row" key={dqKey}>
                                                        <div className={classNames({
                                                            "icon": true,
                                                            "icon-selected": selectedParams?.some(p => p.id === dq.id),
                                                        })} onClick={() => selectDq(dq)}></div>
                                                        <div className="dq">{`${dq?.encoding || ''} ${dq?.name || ''} ${dq?.code || ''}`}</div>
                                                    </div>
                                                )
                                            })}
                                        </>
                                    ))}
                                </div>
                            ))}
                            {list?.length === 0 && <div style={{ marginLeft: 12 }}>暂无可选参数</div>}
                        </>
                    </div>
                    <div className="save-btn" onClick={saveBtnClick}>完成</div>
                </div>}
            </Skeleton>
        </div>
    )
}